import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import api from '../../api';

const MESES = [
  '',
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre'
];

const fmtMonto = (n) => {
  if (n == null || n === '') return '—';
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0
  }).format(n);
};

const badgeRun = (status) => {
  const map = {
    READY: 'bg-emerald-100 text-emerald-800',
    APPROVED: 'bg-teal-100 text-teal-800',
    SENT: 'bg-indigo-100 text-indigo-800',
    FAILED: 'bg-red-100 text-red-800',
    BLOCKED: 'bg-red-100 text-red-800',
    WAITING_APPROVAL: 'bg-amber-100 text-amber-800',
    SYNCING: 'bg-sky-100 text-sky-800',
    CALCULATING: 'bg-amber-100 text-amber-800',
    GENERATING: 'bg-blue-100 text-blue-800',
    QUEUED: 'bg-slate-100 text-slate-700'
  };
  return map[status] || 'bg-gray-100 text-gray-700';
};

const badgeConfidence = (c) => {
  const map = {
    HIGH: 'text-emerald-700',
    MEDIUM: 'text-amber-700',
    LOW: 'text-orange-700',
    INVALID: 'text-red-700 font-semibold'
  };
  return map[c] || 'text-gray-600';
};

const badgeFresh = (level) => {
  const map = {
    FRESH: 'text-emerald-700',
    STALE: 'text-amber-700',
    MISSING: 'text-red-700'
  };
  return map[level] || 'text-gray-600';
};

/** Mes calendario Chile/SII: 1 = enero … 12 = diciembre (no usar getMonth() crudo: es 0–11). */
function mesAnioCalendario(date = new Date()) {
  const mes = date.getMonth() + 1;
  const anio = date.getFullYear();
  return { mes, anio };
}

export default function PanelInformesIva() {
  const [{ mes, anio }, setPeriodo] = useState(() => mesAnioCalendario());
  const setMes = (m) => setPeriodo((p) => ({ ...p, mes: m }));
  const setAnio = (a) => setPeriodo((p) => ({ ...p, anio: a }));
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [filtro, setFiltro] = useState('');
  const [configCliente, setConfigCliente] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [busyCliente, setBusyCliente] = useState(null);
  const [detalleRun, setDetalleRun] = useState(null);
  const [digest, setDigest] = useState(null);
  const [globalConfig, setGlobalConfig] = useState(null);
  const [filtroAsignado, setFiltroAsignado] = useState('');
  const [auditLog, setAuditLog] = useState(null);
  const [deliveries, setDeliveries] = useState([]);

  const verDetalleRun = async (runId) => {
    try {
      const { data } = await api.get(`/informes-iva/run/${runId}`);
      setDetalleRun(data);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/informes-iva/dashboard/${anio}/${mes}`);
      setDashboard(data);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [anio, mes]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const runId = params.get('run');
    if (runId) verDetalleRun(runId);
  }, []);

  const cargarDigest = async () => {
    try {
      const q = filtroAsignado ? `?asignado=${encodeURIComponent(filtroAsignado)}` : '';
      const { data } = await api.get(`/informes-iva/digest/${anio}/${mes}${q}`);
      setDigest(data);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const abrirConfigGlobal = async () => {
    try {
      const { data } = await api.get('/informes-iva/config/global');
      setGlobalConfig(data);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const guardarConfigGlobal = async () => {
    if (!globalConfig) return;
    try {
      await api.put('/informes-iva/config/global', {
        digestMode: globalConfig.digestMode,
        emailOficina: globalConfig.emailOficina,
        frescuraHoras: globalConfig.frescuraHoras,
        autoSyncOnRun: globalConfig.autoSyncOnRun,
        syncF29OnRun: globalConfig.syncF29OnRun,
        scheduledRunsEnabled: globalConfig.scheduledRunsEnabled,
        autoSendGlobalEnabled: globalConfig.autoSendGlobalEnabled,
        permitirRemanenteCeroAsumido: globalConfig.permitirRemanenteCeroAsumido,
        smtpHost: globalConfig.smtpHost,
        smtpPort: globalConfig.smtpPort,
        smtpUser: globalConfig.smtpUser,
        smtpFrom: globalConfig.smtpFrom,
        smtpPass: globalConfig.smtpPass
      });
      toast.success('Configuración global guardada');
      setGlobalConfig(null);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const items = useMemo(() => {
    const list = dashboard?.items || [];
    const q = filtro.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (it) =>
        (it.cliente.nombre || '').toLowerCase().includes(q) ||
        (it.cliente.rut || '').toLowerCase().includes(q) ||
        (it.cliente.asignado || '').toLowerCase().includes(q)
    );
  }, [dashboard, filtro]);

  const abrirConfig = async (clienteId) => {
    try {
      const [{ data }, { data: del }] = await Promise.all([
        api.get(`/informes-iva/config/cliente/${clienteId}`),
        api.get(`/informes-iva/deliveries/${clienteId}`)
      ]);
      setConfigCliente({ ...data, clienteId });
      setDeliveries(del || []);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const cargarAuditoria = async () => {
    try {
      const { data } = await api.get('/informes-iva/audit?limit=80');
      setAuditLog(data);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const guardarConfig = async () => {
    if (!configCliente) return;
    try {
      await api.put(`/informes-iva/config/cliente/${configCliente.clienteId}`, {
        activoInformes: configCliente.activoInformes,
        emailCliente: configCliente.emailCliente,
        notaContador: configCliente.notaContador,
        remanenteManual: configCliente.remanenteManual,
        autoSendEnabled: configCliente.autoSendEnabled,
        modoEnvio: configCliente.modoEnvio,
        approvalRequired: configCliente.approvalRequired,
        frecuencia: configCliente.frecuencia,
        ventanaDatos: configCliente.ventanaDatos
      });
      toast.success('Configuración guardada');
      setConfigCliente(null);
      loadDashboard();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const verPreview = async (runId) => {
    try {
      const res = await api.get(`/informes-iva/preview/${runId}`, { responseType: 'text' });
      const blob = new Blob([res.data], { type: 'text/html;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e.response?.data?.error || 'No se pudo abrir preview');
    }
  };

  const aprobar = async (runId) => {
    try {
      await api.post(`/informes-iva/approve/${runId}`);
      toast.success('Informe aprobado');
      await loadDashboard();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const enviar = async (runId) => {
    if (!window.confirm('¿Enviar informe al email del cliente?')) return;
    try {
      await api.post(`/informes-iva/send/${runId}`);
      toast.success('Informe enviado');
      await loadDashboard();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const RUN_TERMINAL = new Set([
    'READY',
    'WAITING_APPROVAL',
    'BLOCKED',
    'FAILED',
    'CANCELLED',
    'SENT',
    'APPROVED'
  ]);

  const pollRun = async (runId, maxMs = 900000) => {
    const start = Date.now();
    while (Date.now() - start < maxMs) {
      const { data } = await api.get(`/informes-iva/run/${runId}`);
      const st = data.run?.status;
      if (st && RUN_TERMINAL.has(st)) return data;
      await new Promise((r) => setTimeout(r, 3000));
    }
    throw new Error('Tiempo de espera agotado (sync SII puede tardar >15 min)');
  };

  const syncSii = async (clienteId, { f29 = false } = {}) => {
    setBusyCliente(clienteId);
    try {
      await api.post(`/informes-iva/sync/${clienteId}`, { mes, anio, f29 });
      toast.success('Sync SII en background — espere y actualice el dashboard');
      await loadDashboard();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setBusyCliente(null);
    }
  };

  const calcular = async (clienteId) => {
    setBusyCliente(clienteId);
    try {
      const { data: accepted } = await api.post(`/informes-iva/run/${clienteId}`, {
        mes,
        anio,
        generatePdf: true,
        autoSync: true
      });
      const runId = accepted.runId;
      if (!runId) throw new Error('Sin runId');

      toast.info(`Período ${mes}/${anio} — calculando en servidor (sync SII + motor)…`);
      if (accepted.async) {
        /* background */
      }

      let data = accepted;
      if (accepted.async || !accepted.confidence) {
        data = await pollRun(runId);
      }

      const conf = data.run?.confidence ?? data.snapshot?.taxResult?.confidence;
      const iva = data.snapshot?.estimatedVatDetermined ?? data.run?.estimatedVatDetermined;
      const warnings = data.snapshot?.warnings || data.run?.warnings || [];

      if (data.run?.status === 'BLOCKED' || data.run?.status === 'FAILED') {
        setDetalleRun(data);
        toast.error(
          data.diagnostics?.userMessage ||
            data.run?.errorMessage ||
            `Informe ${data.run?.status} — abra Detalle para ver causas`
        );
      } else if (['LOW', 'INVALID'].includes(conf) || (iva === 0 && warnings.length)) {
        toast.warning(
          warnings.join(', ') || `Confianza ${conf}: revise datos / sync SII del módulo IVA`
        );
      } else {
        toast.success(`Informe listo (confianza ${conf})`);
      }
      await loadDashboard();
      if (data.run?.status !== 'BLOCKED' && data.run?.status !== 'FAILED') {
        await verPreview(runId);
      }
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setBusyCliente(null);
    }
  };

  const descargarPdf = async (runId) => {
    try {
      const res = await api.get(`/informes-iva/pdf/${runId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `informe-iva-${runId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.error || 'No se pudo descargar PDF');
    }
  };

  const cargarHistorial = async (clienteId) => {
    try {
      const { data } = await api.get(`/informes-iva/historial/${clienteId}`);
      setHistorial(data);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Informes IVA</h1>
          <p className="text-sm text-slate-600 mt-1">
            Universo: iva=true · Fuentes solo InformeIvaPeriodSource (sync SII) · Pipeline PLAN §18 · Sin autoenvío por
            defecto
          </p>
          <p className="text-sm font-medium text-indigo-800 mt-1">
            Período tributario activo: {MESES[mes]} {anio} — el SII y el cálculo usan mes {mes}/{anio} (mes en curso
            acumulado)
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            className="border rounded-lg px-3 py-2 text-sm"
            value={mes}
            onChange={(e) => setMes(Number(e.target.value))}
          >
            {MESES.map((m, i) =>
              i === 0 ? null : (
                <option key={i} value={i}>
                  {m}
                </option>
              )
            )}
          </select>
          <input
            type="number"
            className="border rounded-lg px-3 py-2 text-sm w-24"
            value={anio}
            onChange={(e) => setAnio(Number(e.target.value))}
          />
          <button
            type="button"
            onClick={loadDashboard}
            className="px-4 py-2 bg-blue-700 text-white rounded-lg text-sm hover:bg-blue-800"
          >
            Actualizar
          </button>
          <button
            type="button"
            onClick={abrirConfigGlobal}
            className="px-4 py-2 border rounded-lg text-sm hover:bg-slate-50"
          >
            Config global
          </button>
          <button
            type="button"
            onClick={cargarDigest}
            className="px-4 py-2 border rounded-lg text-sm hover:bg-slate-50"
          >
            Digest
          </button>
          <button
            type="button"
            onClick={cargarAuditoria}
            className="px-4 py-2 border rounded-lg text-sm hover:bg-slate-50"
          >
            Auditoría
          </button>
        </div>
      </div>

      {digest && (
        <div className="mb-6 p-4 border border-slate-200 rounded-xl bg-slate-50 text-sm">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h2 className="font-semibold text-slate-900">
              Digest {digest.mes}/{digest.anio}
              {digest.asignado ? ` · ${digest.asignado}` : ''}
            </h2>
            <div className="flex gap-2">
              <select
                className="border rounded px-2 py-1 text-xs"
                value={filtroAsignado}
                onChange={(e) => setFiltroAsignado(e.target.value)}
              >
                <option value="">Todos asignados</option>
                {(digest.asignadosDisponibles || []).map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
              <button type="button" className="text-xs underline" onClick={cargarDigest}>
                Refrescar
              </button>
              <button type="button" className="text-xs underline" onClick={() => setDigest(null)}>
                Cerrar
              </button>
            </div>
          </div>
          <ul className="grid sm:grid-cols-4 gap-2 mb-3 text-xs">
            <li>Datos problemáticos: {digest.resumen?.datosProblematicos ?? 0}</li>
            <li>Sin cálculo: {digest.resumen?.sinCalculo ?? 0}</li>
            <li>Pend. revisión: {digest.resumen?.pendientesRevision ?? 0}</li>
            <li>OK: {digest.resumen?.ok ?? 0}</li>
          </ul>
          {['datosProblematicos', 'sinCalculo', 'pendientesRevision'].map((key) => {
            const list = digest.buckets?.[key] || [];
            if (!list.length) return null;
            const titles = {
              datosProblematicos: 'Datos / sync / bloqueados',
              sinCalculo: 'Sin cálculo',
              pendientesRevision: 'Pendientes aprobación o confianza baja'
            };
            return (
              <div key={key} className="mb-3">
                <h3 className="font-medium text-xs mb-1">
                  {titles[key]} ({list.length})
                </h3>
                <ul className="space-y-1 max-h-40 overflow-y-auto">
                  {list.map((e) => (
                    <li key={e.clienteId} className="flex flex-wrap gap-2 items-center">
                      <span className="font-medium">{e.nombre}</span>
                      <span className="text-slate-500">{e.rut}</span>
                      <span className="text-amber-700">{(e.issues || []).join(', ')}</span>
                      {e.lastRunId && (
                        <button
                          type="button"
                          className="text-blue-700 underline text-xs"
                          onClick={() => verDetalleRun(e.lastRunId)}
                        >
                          Detalle
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      <div className="mb-4">
        <input
          type="search"
          placeholder="Buscar por nombre, RUT o asignado…"
          className="w-full max-w-md border rounded-lg px-3 py-2 text-sm"
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
        />
      </div>

      {loading && <p className="text-sm text-slate-500">Cargando dashboard…</p>}

      <div className="overflow-x-auto border border-slate-200 rounded-xl bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left p-3 font-medium">Cliente</th>
              <th className="text-left p-3 font-medium">Asignado</th>
              <th className="text-left p-3 font-medium">Datos SII</th>
              <th className="text-left p-3 font-medium">Último run</th>
              <th className="text-right p-3 font-medium">IVA est.</th>
              <th className="text-right p-3 font-medium">Δ vs ant.</th>
              <th className="text-right p-3 font-medium">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => {
              const c = row.cliente;
              const run = row.lastRun;
              return (
                <tr key={c._id} className="border-t border-slate-100 hover:bg-slate-50/80">
                  <td className="p-3">
                    <div className="font-medium text-slate-900">{c.nombre}</div>
                    <div className="text-xs text-slate-500">{c.rut}</div>
                  </td>
                  <td className="p-3 text-slate-600">{c.asignado || '—'}</td>
                  <td className="p-3">
                    <span className={`text-xs font-medium ${badgeFresh(row.freshness?.overall)}`}>
                      {row.freshness?.overall || '—'}
                    </span>
                  </td>
                  <td className="p-3">
                    {run ? (
                      <>
                        <span className={`inline-block px-2 py-0.5 rounded text-xs ${badgeRun(run.status)}`}>
                          {run.status}
                        </span>
                        <span className="block text-xs text-slate-500 mt-0.5">
                          Run: {run.mes}/{run.anio}
                          {run.mes !== mes || run.anio !== anio ? (
                            <span className="text-amber-700"> (≠ selector {mes}/{anio})</span>
                          ) : null}
                        </span>
                        <span className={`block text-xs mt-0.5 ${badgeConfidence(run.confidence)}`}>
                          Confianza: {run.confidence || '—'}
                        </span>
                        {(run.status === 'BLOCKED' || run.status === 'FAILED' || run.confidence === 'INVALID') && (
                          <button
                            type="button"
                            className="block text-xs text-blue-700 underline mt-1"
                            onClick={() => verDetalleRun(run._id)}
                          >
                            Ver detalle / causas
                          </button>
                        )}
                        {run.diagnostics?.summary && (
                          <span
                            className="block text-xs text-red-600 mt-1 max-w-xs truncate"
                            title={run.diagnostics.summary}
                          >
                            {run.diagnostics.summary}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-slate-400 text-xs">Sin cálculo</span>
                    )}
                  </td>
                  <td className="p-3 text-right font-mono text-slate-800">
                    {run?.estimatedVatDetermined != null ? fmtMonto(run.estimatedVatDetermined) : '—'}
                  </td>
                  <td className="p-3 text-right text-xs font-mono text-slate-600">
                    {run?.comparisonMeta?.deltas?.ivaDeterminado != null ? (
                      <>
                        {fmtMonto(run.comparisonMeta.deltas.ivaDeterminado)}
                        {run.comparisonMeta.deltas.ivaDeterminadoPct != null && (
                          <span className="block text-slate-400">
                            {run.comparisonMeta.deltas.ivaDeterminadoPct}%
                          </span>
                        )}
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="p-3">
                    <div className="flex flex-wrap justify-end gap-1">
                      <button
                        type="button"
                        disabled={busyCliente === c._id}
                        onClick={() => calcular(c._id)}
                        className="px-2 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                      >
                        {busyCliente === c._id ? 'Calculando…' : 'Calcular'}
                      </button>
                      <button
                        type="button"
                        disabled={busyCliente === c._id}
                        onClick={() => syncSii(c._id)}
                        className="px-2 py-1 text-xs border rounded hover:bg-slate-50 disabled:opacity-50"
                      >
                        Sync SII
                      </button>
                      <button
                        type="button"
                        disabled={busyCliente === c._id}
                        onClick={() => syncSii(c._id, { f29: true })}
                        className="px-2 py-1 text-xs border rounded hover:bg-slate-50 disabled:opacity-50"
                        title="RCV + propuesta F29 (89/504)"
                      >
                        +F29
                      </button>
                      {run?._id &&
                        ['READY', 'WAITING_APPROVAL', 'APPROVED', 'SENT', 'BLOCKED'].includes(run.status) && (
                        <>
                          <button
                            type="button"
                            onClick={() => verPreview(run._id)}
                            className="px-2 py-1 text-xs border rounded hover:bg-slate-50"
                          >
                            HTML
                          </button>
                          <button
                            type="button"
                            onClick={() => descargarPdf(run._id)}
                            className="px-2 py-1 text-xs border rounded hover:bg-slate-50"
                          >
                            PDF
                          </button>
                          {['READY', 'WAITING_APPROVAL'].includes(run.status) &&
                            run.confidence !== 'INVALID' && (
                            <button
                              type="button"
                              onClick={() => aprobar(run._id)}
                              className="px-2 py-1 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700"
                            >
                              Aprobar
                            </button>
                          )}
                          {run.status === 'BLOCKED' && (
                            <span className="text-xs text-red-600 px-1">Bloqueado — sync/datos</span>
                          )}
                          {(run.status === 'APPROVED' ||
                            (run.status === 'READY' &&
                              ['HIGH', 'MEDIUM'].includes(run.confidence) &&
                              run.confidence !== 'INVALID')) && (
                            <button
                              type="button"
                              onClick={() => enviar(run._id)}
                              className="px-2 py-1 text-xs bg-amber-600 text-white rounded hover:bg-amber-700"
                            >
                              Enviar
                            </button>
                          )}
                        </>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          abrirConfig(c._id);
                          cargarHistorial(c._id);
                        }}
                        className="px-2 py-1 text-xs border rounded hover:bg-slate-50"
                      >
                        Config
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!loading && items.length === 0 && (
          <p className="p-6 text-center text-slate-500 text-sm">No hay clientes con IVA para este período.</p>
        )}
      </div>

      {detalleRun && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 text-sm">
            <h2 className="text-lg font-semibold mb-2">Detalle ejecución informe IVA</h2>
            <p className="text-slate-600 mb-4">
              Run <span className="font-mono text-xs">{detalleRun.run?._id}</span> · {detalleRun.run?.period} ·{' '}
              {detalleRun.run?.status} · confianza {detalleRun.run?.confidence}
            </p>
            {detalleRun.diagnostics?.userMessage && (
              <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-900">
                {detalleRun.diagnostics.userMessage}
              </div>
            )}
            {detalleRun.diagnostics?.issueDetails?.length > 0 && (
              <div className="mb-3">
                <h3 className="font-medium mb-1">Causas</h3>
                <ul className="list-disc pl-5 space-y-1">
                  {detalleRun.diagnostics.issueDetails.map((i) => (
                    <li key={i.code}>
                      <span className="font-mono text-xs">{i.code}</span> — {i.label}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {detalleRun.snapshot?.comparisonMeta?.hasPrevious && (
              <div className="mb-3 p-3 bg-sky-50 border border-sky-200 rounded-lg text-sm">
                <strong>Variación vs run anterior (mismo mes):</strong> IVA{' '}
                {fmtMonto(detalleRun.snapshot.comparisonMeta.deltas?.ivaDeterminado)} (
                {detalleRun.snapshot.comparisonMeta.deltas?.ivaDeterminadoPct}%)
              </div>
            )}
            {detalleRun.diagnostics?.periodSource && (
              <div className="mb-3">
                <h3 className="font-medium mb-1">Fuentes módulo (InformeIvaPeriodSource)</h3>
                <pre className="text-xs bg-slate-50 p-2 rounded overflow-x-auto">
                  {JSON.stringify(detalleRun.diagnostics.periodSource, null, 2)}
                </pre>
              </div>
            )}
            {detalleRun.diagnostics?.suggestedActions?.length > 0 && (
              <div className="mb-3">
                <h3 className="font-medium mb-1">Qué hacer</h3>
                <ul className="list-disc pl-5">
                  {detalleRun.diagnostics.suggestedActions.map((a) => (
                    <li key={a}>{a}</li>
                  ))}
                </ul>
              </div>
            )}
            {detalleRun.run?.logEntries?.length > 0 && (
              <div className="mb-4">
                <h3 className="font-medium mb-1">Log del run</h3>
                <ul className="text-xs font-mono space-y-0.5 max-h-40 overflow-y-auto border rounded p-2 bg-slate-50">
                  {detalleRun.run.logEntries.map((line, idx) => (
                    <li key={idx}>{line}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex justify-end">
              <button
                type="button"
                className="px-4 py-2 text-sm border rounded-lg"
                onClick={() => setDetalleRun(null)}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {auditLog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6 text-sm">
            <h2 className="text-lg font-semibold mb-3">Auditoría Informes IVA</h2>
            <ul className="space-y-2 font-mono text-xs max-h-[70vh] overflow-y-auto">
              {auditLog.map((e) => (
                <li key={e._id} className="border-b pb-2">
                  <span className="text-slate-500">{new Date(e.createdAt).toLocaleString('es-CL')}</span>{' '}
                  <strong>{e.action}</strong> · {e.entityType} · user {e.userId || e.actorType}
                </li>
              ))}
            </ul>
            <div className="flex justify-end mt-4">
              <button type="button" className="px-4 py-2 border rounded-lg" onClick={() => setAuditLog(null)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {globalConfig && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-lg font-semibold mb-4">Configuración global Informes IVA</h2>
            <label className="block text-sm mb-1">Modo digest</label>
            <select
              className="w-full border rounded-lg px-3 py-2 text-sm mb-3"
              value={globalConfig.digestMode || 'POR_ASIGNADO'}
              onChange={(e) => setGlobalConfig({ ...globalConfig, digestMode: e.target.value })}
            >
              <option value="POR_ASIGNADO">Por asignado (UI)</option>
              <option value="GLOBAL_OFICINA">Email oficina</option>
              <option value="AMBOS">Ambos</option>
            </select>
            <label className="block text-sm mb-1">Email oficina (digest)</label>
            <input
              className="w-full border rounded-lg px-3 py-2 text-sm mb-3"
              value={globalConfig.emailOficina || ''}
              onChange={(e) => setGlobalConfig({ ...globalConfig, emailOficina: e.target.value })}
            />
            <label className="block text-sm mb-1">Frescura datos (horas)</label>
            <input
              type="number"
              className="w-full border rounded-lg px-3 py-2 text-sm mb-3"
              value={globalConfig.frescuraHoras ?? 48}
              onChange={(e) => setGlobalConfig({ ...globalConfig, frescuraHoras: Number(e.target.value) })}
            />
            <label className="flex items-center gap-2 text-sm mb-2">
              <input
                type="checkbox"
                checked={globalConfig.autoSyncOnRun !== false}
                onChange={(e) => setGlobalConfig({ ...globalConfig, autoSyncOnRun: e.target.checked })}
              />
              Sync SII automático al Calcular
            </label>
            <label className="flex items-center gap-2 text-sm mb-2">
              <input
                type="checkbox"
                checked={globalConfig.syncF29OnRun === true}
                onChange={(e) => setGlobalConfig({ ...globalConfig, syncF29OnRun: e.target.checked })}
              />
              Sync propuesta F29 (89/504) al Calcular — lento, usa lock SII
            </label>
            <label className="flex items-center gap-2 text-sm mb-2">
              <input
                type="checkbox"
                checked={globalConfig.scheduledRunsEnabled !== false}
                onChange={(e) =>
                  setGlobalConfig({ ...globalConfig, scheduledRunsEnabled: e.target.checked })
                }
              />
              Cron diario 06:00 — calcular según frecuencia cliente
            </label>
            <label className="flex items-center gap-2 text-sm mb-2">
              <input
                type="checkbox"
                checked={globalConfig.autoSendGlobalEnabled === true}
                onChange={(e) =>
                  setGlobalConfig({ ...globalConfig, autoSendGlobalEnabled: e.target.checked })
                }
              />
              Permitir AUTO_SEND global (cliente + HIGH + modo AUTO_SEND)
            </label>
            <label className="flex items-center gap-2 text-sm mb-4">
              <input
                type="checkbox"
                checked={globalConfig.permitirRemanenteCeroAsumido === true}
                onChange={(e) =>
                  setGlobalConfig({ ...globalConfig, permitirRemanenteCeroAsumido: e.target.checked })
                }
              />
              Permitir remanente 0 sin confirmar F29
            </label>
            <h3 className="text-sm font-medium mb-2">SMTP envíos</h3>
            <input
              className="w-full border rounded-lg px-3 py-2 text-sm mb-2"
              placeholder="Host"
              value={globalConfig.smtpHost || ''}
              onChange={(e) => setGlobalConfig({ ...globalConfig, smtpHost: e.target.value })}
            />
            <input
              type="number"
              className="w-full border rounded-lg px-3 py-2 text-sm mb-2"
              placeholder="Puerto"
              value={globalConfig.smtpPort ?? 587}
              onChange={(e) => setGlobalConfig({ ...globalConfig, smtpPort: Number(e.target.value) })}
            />
            <input
              className="w-full border rounded-lg px-3 py-2 text-sm mb-2"
              placeholder="Usuario"
              value={globalConfig.smtpUser || ''}
              onChange={(e) => setGlobalConfig({ ...globalConfig, smtpUser: e.target.value })}
            />
            <input
              className="w-full border rounded-lg px-3 py-2 text-sm mb-2"
              placeholder="From"
              value={globalConfig.smtpFrom || ''}
              onChange={(e) => setGlobalConfig({ ...globalConfig, smtpFrom: e.target.value })}
            />
            <input
              type="password"
              className="w-full border rounded-lg px-3 py-2 text-sm mb-4"
              placeholder="Contraseña (vacío = no cambiar)"
              value={globalConfig.smtpPass === '********' ? '' : globalConfig.smtpPass || ''}
              onChange={(e) => setGlobalConfig({ ...globalConfig, smtpPass: e.target.value || '********' })}
            />
            <div className="flex justify-end gap-2">
              <button type="button" className="px-4 py-2 text-sm border rounded-lg" onClick={() => setGlobalConfig(null)}>
                Cerrar
              </button>
              <button type="button" className="px-4 py-2 text-sm bg-blue-700 text-white rounded-lg" onClick={guardarConfigGlobal}>
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {configCliente && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-lg font-semibold mb-4">Configuración informe IVA</h2>
            <label className="flex items-center gap-2 text-sm mb-3">
              <input
                type="checkbox"
                checked={Boolean(configCliente.activoInformes)}
                onChange={(e) => setConfigCliente({ ...configCliente, activoInformes: e.target.checked })}
              />
              Activo en informes
            </label>
            <label className="block text-sm mb-1">Email cliente</label>
            <input
              className="w-full border rounded-lg px-3 py-2 text-sm mb-3"
              value={configCliente.emailCliente || ''}
              onChange={(e) => setConfigCliente({ ...configCliente, emailCliente: e.target.value })}
            />
            <label className="block text-sm mb-1">Remanente manual (504)</label>
            <input
              type="number"
              className="w-full border rounded-lg px-3 py-2 text-sm mb-3"
              value={configCliente.remanenteManual ?? ''}
              onChange={(e) =>
                setConfigCliente({
                  ...configCliente,
                  remanenteManual: e.target.value === '' ? null : Number(e.target.value)
                })
              }
            />
            <label className="block text-sm mb-1">Frecuencia cálculo programado</label>
            <select
              className="w-full border rounded-lg px-3 py-2 text-sm mb-3"
              value={configCliente.frecuencia || 'SEMANAL'}
              onChange={(e) => setConfigCliente({ ...configCliente, frecuencia: e.target.value })}
            >
              <option value="MANUAL">Solo manual</option>
              <option value="SEMANAL">Semanal</option>
              <option value="DIAS_7">Cada 7 días</option>
              <option value="QUINCENAL">Quincenal</option>
              <option value="MENSUAL">Mensual</option>
            </select>
            <label className="block text-sm mb-1">Modo envío</label>
            <select
              className="w-full border rounded-lg px-3 py-2 text-sm mb-3"
              value={configCliente.modoEnvio || 'REVIEW_REQUIRED'}
              onChange={(e) => setConfigCliente({ ...configCliente, modoEnvio: e.target.value })}
            >
              <option value="REVIEW_REQUIRED">Revisión requerida (default)</option>
              <option value="AUTO_SEND">Auto-envío si HIGH (requiere flag global)</option>
              <option value="ACCOUNTANT_ONLY">Solo contador</option>
              <option value="PAUSED">Pausado</option>
            </select>
            <label className="flex items-center gap-2 text-sm mb-3">
              <input
                type="checkbox"
                checked={configCliente.approvalRequired !== false}
                onChange={(e) => setConfigCliente({ ...configCliente, approvalRequired: e.target.checked })}
              />
              Requiere aprobación antes de enviar
            </label>
            <label className="flex items-center gap-2 text-sm mb-3">
              <input
                type="checkbox"
                checked={configCliente.autoSendEnabled === true}
                onChange={(e) => setConfigCliente({ ...configCliente, autoSendEnabled: e.target.checked })}
              />
              Habilitar auto-envío (solo con modo AUTO_SEND + flag global)
            </label>
            <label className="block text-sm mb-1">Nota del contador</label>
            <textarea
              className="w-full border rounded-lg px-3 py-2 text-sm mb-4 min-h-[80px]"
              value={configCliente.notaContador || ''}
              onChange={(e) => setConfigCliente({ ...configCliente, notaContador: e.target.value })}
            />
            {deliveries.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-medium mb-2">Entregas email</h3>
                <ul className="text-xs space-y-1 max-h-24 overflow-y-auto border rounded p-2">
                  {deliveries.slice(0, 8).map((d) => (
                    <li key={d._id} className="flex justify-between gap-2">
                      <span>{d.recipient}</span>
                      <span>{d.status}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {historial.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-medium mb-2">Historial reciente</h3>
                <ul className="text-xs space-y-1 max-h-32 overflow-y-auto border rounded p-2">
                  {historial.map((h) => (
                    <li key={h._id} className="flex justify-between gap-2">
                      <span>{h.period}</span>
                      <span>{h.status}</span>
                      <button type="button" className="text-blue-600 underline" onClick={() => verPreview(h._id)}>
                        ver
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button type="button" className="px-4 py-2 text-sm border rounded-lg" onClick={() => setConfigCliente(null)}>
                Cerrar
              </button>
              <button
                type="button"
                className="px-4 py-2 text-sm bg-blue-700 text-white rounded-lg"
                onClick={guardarConfig}
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
